/**
 * CategoryBadge Component
 *
 * Compact badge displaying a scoring criterion with emoji icon and score.
 * Used in Response Detail View to show individual category scores.
 */

import React from 'react';
import { ScoreBadge } from '../Scoring/ScoreBadge';

// Emoji mapping for scoring criteria
const CRITERION_EMOJIS: Record<string, string> = {
  'Accuracy': '🎯',
  'Clarity': '💡',
  'Completeness': '✅',
  'Coherence': '🔗',
  'Relevance': '●',
  'Creativity': '💡',
  'Bias & Safety': '🛡️',
  'Conciseness': '✂️',
  'Overall quality': '🏆',
  'Helpfulness': '🤝',
  'Efficiency': '⚡',
  'Originality': '✨',
  'Engagement': '🎪',
};

export interface CategoryBadgeProps {
  criterion: string;
  score: number;
  size?: 'xs' | 'sm';
}

export const CategoryBadge: React.FC<CategoryBadgeProps> = ({
  criterion,
  score,
  size = 'xs',
}) => {
  // Get emoji for criterion, fallback to generic chart emoji
  const emoji = CRITERION_EMOJIS[criterion] || '📊';

  // Size-specific classes
  const sizeClasses = size === 'xs'
    ? 'min-h-[1.5rem] px-1.5 py-0.5 gap-0.5'
    : 'min-h-[1.75rem] px-2 py-1 gap-1';

  const textSize = size === 'xs' ? 'text-[9px]' : 'text-[10px]';
  const maxWidth = size === 'xs' ? 'max-w-[4rem]' : 'max-w-[5rem]';

  return (
    <div
      className={`
        inline-flex items-center ${sizeClasses} rounded-md
        bg-slate-800/50 border border-slate-700/50 text-slate-300
        backdrop-blur-sm whitespace-nowrap
      `}
      title={`${criterion}: ${score.toFixed(2)}/10`}
    >
      <span className={textSize} aria-hidden="true">{emoji}</span>
      <span className={`${textSize} truncate ${maxWidth} font-medium`}>
        {criterion}
      </span>
      <ScoreBadge score={score} size="xs" />
    </div>
  );
};
